import React, { useEffect, useState } from 'react';
import {
  Box,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  Alert,
  CircularProgress,
  Typography,
} from '@mui/material';
import { VisibilityOff, Visibility, ArrowForward } from '@mui/icons-material';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { object, string, ref } from 'yup';
import { useFormik } from 'formik';
import axios from 'axios';
import { useUser } from '../context/UserContext';
import { completeOnboarding, fetchOnboardingPreview } from '../api/onboarding';
import type { OnboardingPreview } from '../api/onboarding';
import { DEFAULT_DIAL_CODE } from '../constants/phoneDialCodes';
import { UPLOAD_INE_PATH } from '../routes/routePaths';
import urbanImage from '../assets/urban_logo.png';
import PhoneDialField from './PhoneDialField';

const ACCENT = '#3B82F6';
const BACKGROUND_LIGHT = '#F9FAFB';

interface FinishOnboardingForm {
  email: string;
  dialCode: string;
  phone: string;
  password: string;
  confirmPassword: string;
}

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: 'rgba(248, 250, 252, 1)',
    '& fieldset': { border: 'none' },
    '&.Mui-focused': {
      boxShadow: `0 0 0 2px ${ACCENT}`,
      backgroundColor: 'rgba(248, 250, 252, 1)',
    },
    '&.Mui-error': {
      boxShadow: '0 0 0 2px #d32f2f',
    },
  },
  '& .MuiOutlinedInput-input': {
    padding: '14px 16px',
  },
};

function FinishOnboarding(): React.ReactElement {
  const navigate = useNavigate();
  const { setAuth } = useUser();
  const { token = '' } = useParams<{ token?: string }>();
  const [preview, setPreview] = useState<OnboardingPreview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    fetchOnboardingPreview(token)
      .then((data) => {
        if (!cancelled) setPreview(data);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (axios.isAxiosError(err) && err.response?.status === 410) {
          setLoadError(err.response.data?.message ?? 'Este enlace expiró o ya fue usado.');
        } else {
          setLoadError('No pudimos abrir este enlace. Pide uno nuevo al equipo Urban.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const formik = useFormik<FinishOnboardingForm>({
    enableReinitialize: true,
    initialValues: {
      email: '',
      dialCode: preview?.dialCode || DEFAULT_DIAL_CODE,
      phone: preview?.phoneNational || '',
      password: '',
      confirmPassword: '',
    },
    validationSchema: object({
      email: string().required('El email es obligatorio').email('Ingresa un email válido'),
      dialCode: string().required('Selecciona un código de país'),
      phone: string()
        .required('El teléfono es obligatorio')
        .when('dialCode', {
          is: DEFAULT_DIAL_CODE,
          then: (schema) => schema.length(10, 'El teléfono debe tener 10 dígitos'),
          otherwise: (schema) =>
            schema.min(8, 'Ingresa un teléfono válido').max(12, 'Ingresa un teléfono válido'),
        }),
      password: string()
        .required('La contraseña es obligatoria')
        .min(5, 'La contraseña debe tener al menos 5 caracteres'),
      confirmPassword: string()
        .required('Confirma tu contraseña')
        .oneOf([ref('password')], 'Las contraseñas no coinciden'),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitError(null);
      try {
        const data = await completeOnboarding(token, {
          email: values.email,
          password: values.password,
          dialCode: values.dialCode,
          phone: values.phone,
        });
        if (data?.data?.token && data?.data?.user) {
          setAuth(data.data.token, data.data.user);
          navigate(UPLOAD_INE_PATH);
        } else {
          setSubmitError('No se pudo completar el registro. Intenta de nuevo.');
        }
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.data?.message) {
          setSubmitError(String(err.response.data.message));
        } else {
          setSubmitError('No se pudo completar el registro. Intenta de nuevo.');
        }
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: BACKGROUND_LIGHT,
        color: 'text.primary',
      }}
    >
      <Box
        component="header"
        sx={{
          width: '100%',
          py: 3,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <img
          src={urbanImage}
          alt="Urban Utility Logo"
          style={{ width: 320, objectFit: 'contain', opacity: 0.9 }}
        />
      </Box>

      <Box
        component="main"
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          pb: 4,
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 448,
            bgcolor: 'white',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            borderRadius: '16px',
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'rgba(226, 232, 240, 1)',
          }}
        >
          <Box sx={{ px: 3, pt: 3 }}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}
            >
              <Typography
                component="span"
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: ACCENT,
                }}
              >
                Paso 1 de 2
              </Typography>
              <Typography
                component="span"
                sx={{ fontSize: 12, fontWeight: 500, color: 'text.secondary' }}
              >
                50% Completado
              </Typography>
            </Box>
            <Box
              sx={{
                width: '100%',
                height: 6,
                borderRadius: 9999,
                bgcolor: 'rgba(241, 245, 249, 1)',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  width: '50%',
                  height: '100%',
                  bgcolor: ACCENT,
                  borderRadius: 9999,
                }}
              />
            </Box>
          </Box>

          <Box sx={{ p: 3 }}>
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            )}
            {!loading && loadError && (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {loadError}
              </Alert>
            )}
            {!loading && !loadError && (
              <Box component="form" autoComplete="off" noValidate onSubmit={formik.handleSubmit}>
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, letterSpacing: '-0.025em', mb: 0.5 }}
                  >
                    Termina tu registro
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: 14 }}>
                    Hola {preview?.firstName}, ya tenemos tu inventario. Confirma tu WhatsApp,
                    agrega tu correo y una contraseña para entrar al portal.
                  </Typography>
                </Box>

                {submitError ? (
                  <Alert
                    severity="error"
                    onClose={() => setSubmitError(null)}
                    sx={{ mb: 2, borderRadius: 2 }}
                  >
                    {submitError}
                  </Alert>
                ) : null}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  <Box>
                    <Typography
                      component="label"
                      htmlFor="email"
                      sx={{
                        display: 'block',
                        fontSize: 14,
                        fontWeight: 500,
                        mb: 1,
                        textAlign: 'left',
                      }}
                    >
                      Correo electrónico
                    </Typography>
                    <TextField
                      id="email"
                      name="email"
                      fullWidth
                      type="email"
                      placeholder="ejemplo@correo.com"
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.email && Boolean(formik.errors.email)}
                      helperText={formik.touched.email ? formik.errors.email : ''}
                      variant="outlined"
                      sx={inputSx}
                    />
                  </Box>
                  <PhoneDialField
                    id="phone"
                    dialCode={formik.values.dialCode}
                    national={formik.values.phone}
                    onDialCodeChange={(code) => formik.setFieldValue('dialCode', code)}
                    onNationalChange={(digits) => formik.setFieldValue('phone', digits)}
                    onBlur={() => formik.setFieldTouched('phone', true)}
                    error={Boolean(formik.touched.phone && formik.errors.phone)}
                    helperText={
                      formik.touched.phone && formik.errors.phone ? formik.errors.phone : ''
                    }
                  />
                  <Box>
                    <Typography
                      component="label"
                      htmlFor="password"
                      sx={{
                        display: 'block',
                        fontSize: 14,
                        fontWeight: 500,
                        mb: 1,
                        textAlign: 'left',
                      }}
                    >
                      Contraseña
                    </Typography>
                    <TextField
                      id="password"
                      name="password"
                      fullWidth
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={formik.values.password}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.password && Boolean(formik.errors.password)}
                      helperText={formik.touched.password ? formik.errors.password : ''}
                      variant="outlined"
                      sx={inputSx}
                      slotProps={{
                        input: {
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label={
                                  showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'
                                }
                                onClick={() => setShowPassword((v) => !v)}
                                edge="end"
                                size="small"
                              >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        },
                      }}
                    />
                  </Box>
                  <Box>
                    <Typography
                      component="label"
                      htmlFor="confirmPassword"
                      sx={{
                        display: 'block',
                        fontSize: 14,
                        fontWeight: 500,
                        mb: 1,
                        textAlign: 'left',
                      }}
                    >
                      Confirmar contraseña
                    </Typography>
                    <TextField
                      id="confirmPassword"
                      name="confirmPassword"
                      fullWidth
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={formik.values.confirmPassword}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)
                      }
                      helperText={
                        formik.touched.confirmPassword ? formik.errors.confirmPassword : ''
                      }
                      variant="outlined"
                      sx={inputSx}
                      slotProps={{
                        input: {
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label={
                                  showConfirmPassword
                                    ? 'Ocultar confirmación'
                                    : 'Mostrar confirmación'
                                }
                                onClick={() => setShowConfirmPassword((v) => !v)}
                                edge="end"
                                size="small"
                              >
                                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        },
                      }}
                    />
                  </Box>
                </Box>

                <Button
                  type="submit"
                  fullWidth
                  disabled={formik.isSubmitting}
                  sx={{
                    mt: 3,
                    py: 2,
                    bgcolor: '#000',
                    color: 'white',
                    fontWeight: 700,
                    borderRadius: '12px',
                    '&:hover': { bgcolor: '#1e293b' },
                    '&:disabled': { bgcolor: '#94a3b8', color: 'white' },
                  }}
                  startIcon={
                    formik.isSubmitting ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <ArrowForward sx={{ fontSize: 20 }} />
                    )
                  }
                >
                  {formik.isSubmitting ? 'Guardando...' : 'Continuar'}
                </Button>
              </Box>
            )}
          </Box>

          <Box sx={{ px: 3, pb: 3, textAlign: 'center' }}>
            <Typography component="span" sx={{ fontSize: 12, color: 'text.secondary' }}>
              ¿Ya terminaste tu registro?{' '}
            </Typography>
            <Typography
              component={Link}
              to="/login"
              sx={{
                fontSize: 12,
                fontWeight: 600,
                color: ACCENT,
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Inicia Sesión
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box
        component="footer"
        sx={{
          py: 2,
          textAlign: 'center',
          fontSize: 10,
          color: 'text.secondary',
          textTransform: 'uppercase',
          letterSpacing: '0.2em',
        }}
      >
        Urban Utility Guadalajara © {new Date().getFullYear()}
      </Box>
    </Box>
  );
}

export default FinishOnboarding;
